import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface MatchResultRow {
  team1_player1: string | null;
  team1_player2: string | null;
  team2_player1: string | null;
  team2_player2: string | null;
  winner_team: number | null;
  score: string | null;
  phase: string | null;
  group_name: string | null;
  match_number: number | null;
  category: string | null;
  subcategory: string | null;
}

export interface GroupMatchResult {
  /** Ganador expresado respecto al par consultado: 'A' | 'B' | 'draw' */
  winnerSide: 'A' | 'B' | 'draw' | null;
  score?: string;
  matchNumber?: number | null;
}

/**
 * Normaliza nombres de equipo/jugador para comparar.
 * Mismo criterio que useTournamentSchedule para que ambos coincidan.
 */
const normalizeName = (name?: string | null): string =>
  (name || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s*\/\s*/g, ' / ')
    .replace(/\s*-\s*/g, ' / ')
    .replace(/\s+/g, ' ');

/**
 * Si viene category="Masculino 3era" sin subcategory, la separa.
 * (mismo criterio que useGroupStandings)
 */
function splitCategory(inputCategory?: string | null, inputSubcategory?: string | null) {
  if (inputSubcategory && inputSubcategory.trim()) {
    return { category: (inputCategory || '').trim(), subcategory: inputSubcategory.trim() };
  }
  const trimmed = (inputCategory || '').trim();
  const parts = trimmed.split(' ');
  if (parts.length >= 2) {
    const subcategory = parts.pop()!;
    return { category: parts.join(' '), subcategory };
  }
  return { category: trimmed, subcategory: undefined as string | undefined };
}

/**
 * Lee los resultados de fase de grupos directamente de `tournament_match_results`,
 * filtrados por grupo + categoría + subcategoría.
 *
 * Esto reemplaza la resolución indirecta vía `tournament_schedules.groupBlocks`
 * (que reconstruye los cruces A/B/C por índice y falla cuando el orden de equipos
 * del grupo no es denso — p. ej. huecos `placeholder-gap-*` — o cuando no hay
 * calendario de grupo para esa categoría). Al consultar por nombres reales,
 * un usuario inscrito en varias categorías ve el resultado correcto en cada una.
 */
export function useGroupMatchResults(
  eventId: string,
  groupName?: string | null,
  category?: string | null,
  subcategory?: string | null
) {
  const [results, setResults] = useState<MatchResultRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchResults = async () => {
      if (!eventId || !groupName) {
        setResults([]);
        setLoading(false);
        return;
      }

      const normalized = splitCategory(category, subcategory);

      try {
        let query = supabase
          .from('tournament_match_results')
          .select('team1_player1, team1_player2, team2_player1, team2_player2, winner_team, score, phase, group_name, match_number, category, subcategory')
          .eq('tournament_id', eventId)
          .eq('group_name', groupName);

        if (normalized.category) query = query.eq('category', normalized.category);
        if (normalized.subcategory) query = query.eq('subcategory', normalized.subcategory);

        const { data, error } = await query;
        if (error) throw error;
        if (cancelled) return;

        // Sólo partidos de fase de grupos
        const groupPhase = (data || []).filter(r => (r.phase || '').toLowerCase().includes('grupo'));
        setResults(groupPhase);
      } catch (err) {
        console.error('[useGroupMatchResults] Error:', err);
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    setLoading(true);
    fetchResults();

    const channel = supabase
      .channel(`group-match-results-${eventId}-${groupName}-${category}-${subcategory}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'tournament_match_results', filter: `tournament_id=eq.${eventId}` },
        () => fetchResults()
      )
      .subscribe();

    const pollInterval = setInterval(fetchResults, 30000);

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
      clearInterval(pollInterval);
    };
  }, [eventId, groupName, category, subcategory]);

  /**
   * Busca el resultado del cruce (teamA vs teamB) por nombre completo del equipo.
   * Devuelve el ganador ya expresado respecto a teamA/teamB.
   */
  const findResult = useCallback(
    (teamA?: string | null, teamB?: string | null): GroupMatchResult | null => {
      const a = normalizeName(teamA);
      const b = normalizeName(teamB);
      if (!a || !b) return null;

      for (const r of results) {
        const t1 = normalizeName(
          r.team1_player2 ? `${r.team1_player1} / ${r.team1_player2}` : r.team1_player1
        );
        const t2 = normalizeName(
          r.team2_player2 ? `${r.team2_player1} / ${r.team2_player2}` : r.team2_player1
        );
        if (!t1 || !t2) continue;

        const straight = t1 === a && t2 === b;
        const swapped = t1 === b && t2 === a;
        if (!straight && !swapped) continue;

        let winnerSide: 'A' | 'B' | 'draw' | null = null;
        if (r.winner_team === 0) {
          winnerSide = 'draw';
        } else if (r.winner_team === 1) {
          winnerSide = straight ? 'A' : 'B';
        } else if (r.winner_team === 2) {
          winnerSide = straight ? 'B' : 'A';
        }

        return {
          winnerSide,
          score: r.score || undefined,
          matchNumber: r.match_number ?? null,
        };
      }

      return null;
    },
    [results]
  );

  return { results, findResult, loading };
}

export interface GroupResultScope {
  groupName?: string | null;
  category?: string | null;
  subcategory?: string | null;
}

/**
 * Variante de [useGroupMatchResults] para cuando hace falta consultar **varios grupos
 * del mismo evento** en un solo hook (p. ej. un usuario inscrito en varias categorías).
 *
 * Trae todos los resultados de fase de grupos del torneo y deja el filtrado por
 * grupo/categoría a `findResult`, que recibe el ámbito de cada partido. Así se evita
 * llamar a un hook dentro de un bucle.
 */
export function useEventGroupMatchResults(eventId: string) {
  const [results, setResults] = useState<MatchResultRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchResults = async () => {
      if (!eventId) {
        setResults([]);
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('tournament_match_results')
          .select('team1_player1, team1_player2, team2_player1, team2_player2, winner_team, score, phase, group_name, match_number, category, subcategory')
          .eq('tournament_id', eventId);

        if (error) throw error;
        if (cancelled) return;

        setResults((data || []).filter(r => (r.phase || '').toLowerCase().includes('grupo')));
      } catch (err) {
        console.error('[useEventGroupMatchResults] Error:', err);
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    setLoading(true);
    fetchResults();

    const channel = supabase
      .channel(`event-group-results-${eventId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'tournament_match_results', filter: `tournament_id=eq.${eventId}` },
        () => fetchResults()
      )
      .subscribe();

    const pollInterval = setInterval(fetchResults, 30000);

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
      clearInterval(pollInterval);
    };
  }, [eventId]);

  const findResult = useCallback(
    (teamA?: string | null, teamB?: string | null, scope?: GroupResultScope): GroupMatchResult | null => {
      const a = normalizeName(teamA);
      const b = normalizeName(teamB);
      if (!a || !b) return null;

      const wanted = scope
        ? splitCategory(scope.category, scope.subcategory)
        : { category: '', subcategory: undefined as string | undefined };
      const wantedGroup = normalizeName(scope?.groupName);

      for (const r of results) {
        // Acotar al grupo/categoría del partido cuando se conocen, para que dos
        // categorías con el mismo cruce de nombres no se pisen entre sí.
        if (wantedGroup && normalizeName(r.group_name) !== wantedGroup) continue;
        if (wanted.category && normalizeName(r.category) !== normalizeName(wanted.category)) continue;
        if (wanted.subcategory && normalizeName(r.subcategory) !== normalizeName(wanted.subcategory)) continue;

        const t1 = normalizeName(
          r.team1_player2 ? `${r.team1_player1} / ${r.team1_player2}` : r.team1_player1
        );
        const t2 = normalizeName(
          r.team2_player2 ? `${r.team2_player1} / ${r.team2_player2}` : r.team2_player1
        );
        if (!t1 || !t2) continue;

        const straight = t1 === a && t2 === b;
        const swapped = t1 === b && t2 === a;
        if (!straight && !swapped) continue;

        let winnerSide: 'A' | 'B' | 'draw' | null = null;
        if (r.winner_team === 0) {
          winnerSide = 'draw';
        } else if (r.winner_team === 1) {
          winnerSide = straight ? 'A' : 'B';
        } else if (r.winner_team === 2) {
          winnerSide = straight ? 'B' : 'A';
        }

        return {
          winnerSide,
          score: r.score || undefined,
          matchNumber: r.match_number ?? null,
        };
      }

      return null;
    },
    [results]
  );

  return { results, findResult, loading };
}
